import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useFinance } from '@/hooks/useFinance';
import { useCompanyProfile } from '@/hooks/useCompanyProfile';
import { useMerchantClients } from '@/hooks/useMerchantClients';
import { buildInvoiceNumber } from '@/lib/companyProfile';
import { getOrganizationByCommerce } from '@/lib/zifekCore';
import { ensureNumberingConfig, reserveEntityNumber } from '@/lib/coreSystem';
import { formatMoney } from '@/lib/finance';
import {
  DOC_META,
  buildFallbackNumber,
  computeTotals,
  dateInDays,
  emptyLine,
  type DocType,
  type InvoiceLineDraft,
} from '@/lib/invoiceDoc';
import { Field, inputClass } from '@/pages/dashboard/finance/components/FinanceUI';
import InvoiceHeaderBlock from '@/pages/dashboard/finance/components/InvoiceHeaderBlock';
import InvoiceComplianceBlock from '@/pages/dashboard/finance/components/InvoiceComplianceBlock';
import ClientPickerBlock, { type CustomerFields } from '@/pages/dashboard/finance/components/ClientPickerBlock';
import LineItemsEditor, { type CatalogItem } from '@/pages/dashboard/finance/components/LineItemsEditor';
import InvoiceTotalsBlock from '@/pages/dashboard/finance/components/InvoiceTotalsBlock';

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function InvoiceEditor({ docType }: { docType: DocType }) {
  const meta = DOC_META[docType];
  const isQuote = docType === 'quote';
  const navigate = useNavigate();
  const finance = useFinance();
  const company = useCompanyProfile();
  const { clients, reload: reloadClients } = useMerchantClients();
  const profile = company.profile;
  const currency = finance.currency;

  const [customer, setCustomer] = useState<CustomerFields>({ name: '', phone: '', email: '', address: '', ice: '' });
  const [issueDate, setIssueDate] = useState(todayIso());
  const [saleDate, setSaleDate] = useState('');
  const [dueDate, setDueDate] = useState(dateInDays(30));
  const [reference, setReference] = useState('');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [mentions, setMentions] = useState('');
  const [discount, setDiscount] = useState(0);
  const [taxRate, setTaxRate] = useState(20);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<InvoiceLineDraft[]>([emptyLine()]);
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const taxTouched = useRef(false);

  // Pré-remplissage depuis les paramètres de facturation.
  useEffect(() => {
    setPaymentTerms((prev) => prev || profile.payment_terms || '');
    setMentions((prev) => prev || profile.legal_mentions || '');
  }, [profile.payment_terms, profile.legal_mentions]);

  useEffect(() => {
    if (taxTouched.current) return;
    // TVA appliquée uniquement si le commerce est assujetti (Paramètres facturation).
    setTaxRate(profile.vat_subject ? profile.default_vat_rate : 0);
  }, [profile.default_vat_rate, profile.vat_subject]);

  // Catalogue produits + services du marchand.
  const merchantId = finance.merchantId;
  useEffect(() => {
    if (!merchantId) {
      setCatalogLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      setCatalogLoading(true);
      try {
        const [prodRes, servRes] = await Promise.all([
          supabase
            .from('product_items')
            .select('id, name, price, description')
            .eq('idcommerce', merchantId)
            .order('name')
            .limit(200),
          supabase
            .from('nospartenairesservices')
            .select('id, titre, prix, description')
            .eq('idcommerce', merchantId)
            .order('titre')
            .limit(200),
        ]);
        if (!alive) return;
        const products: CatalogItem[] = (prodRes.data || []).map((p: Record<string, unknown>) => ({
          id: Number(p.id),
          name: String(p.name || 'Produit'),
          price: Number(p.price) || 0,
          description: String(p.description || ''),
          kind: 'product',
        }));
        const services: CatalogItem[] = (servRes.data || []).map((s: Record<string, unknown>) => ({
          id: Number(s.id),
          name: String(s.titre || 'Service'),
          price: Number(s.prix) || 0,
          description: String(s.description || ''),
          kind: 'service',
        }));
        setCatalog([...products, ...services]);
      } catch {
        if (alive) setCatalog([]);
      } finally {
        if (alive) setCatalogLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [merchantId]);

  const totals = useMemo(() => computeTotals(lines, discount, taxRate), [lines, discount, taxRate]);

  const numberPreview = useMemo(() => {
    if (!isQuote) return buildInvoiceNumber(profile, profile.next_invoice_seq || 1);
    const sameKind = finance.invoices.filter((i) => (i.doc_type || 'invoice') === docType);
    return buildFallbackNumber(docType, sameKind.length + 1);
  }, [isQuote, profile, finance.invoices, docType]);

  const reserveNumber = async (): Promise<string> => {
    // Les devis ne consomment PAS la séquence légale des factures.
    if (isQuote) {
      try {
        if (merchantId) {
          const org = await getOrganizationByCommerce(merchantId);
          if (org) {
            await ensureNumberingConfig({
              tenantId: org.tenant_id,
              organizationId: org.id,
              entityType: 'quote',
              prefix: 'DEV',
              padding: 5,
              useYear: true,
            });
            const reserved = await reserveEntityNumber(org.id, 'quote');
            if (reserved) return reserved;
          }
        }
      } catch {
        /* repli ci-dessous */
      }
      return buildFallbackNumber('quote', finance.invoices.filter((i) => (i.doc_type || 'invoice') === 'quote').length + 1);
    }

    try {
      if (merchantId) {
        const org = await getOrganizationByCommerce(merchantId);
        if (org) {
          await ensureNumberingConfig({
            tenantId: org.tenant_id,
            organizationId: org.id,
            entityType: 'invoice',
            prefix: finance.settings?.invoice_prefix || 'FAC',
            padding: 5,
            useYear: true,
          });
          const reserved = await reserveEntityNumber(org.id, 'invoice');
          if (reserved) return reserved;
        }
      }
    } catch {
      /* repli ci-dessous */
    }
    try {
      const fallback = await company.reserveInvoiceNumber(buildInvoiceNumber);
      return fallback.number;
    } catch {
      return buildFallbackNumber('invoice', finance.invoices.filter((i) => (i.doc_type || 'invoice') === 'invoice').length + 1);
    }
  };

  const handleSave = async () => {
    setError(null);
    // Conformité Art. 145 : une facture doit porter l'ICE et l'IF de l'émetteur.
    if (!isQuote && (!profile.ice.trim() || !profile.if_number.trim())) {
      setError(
        'Facture non conforme (Art. 145) : renseignez votre ICE (15 chiffres) et votre IF dans Paramètres facturation avant d’émettre une facture.',
      );
      return;
    }
    if (!customer.name.trim()) {
      setError('Le nom du client est obligatoire (bloc 2).');
      return;
    }
    const validLines = lines.filter((l) => l.description.trim());
    if (validLines.length === 0) {
      setError('Ajoutez au moins une ligne de produit ou de service (bloc 4).');
      return;
    }
    setBusy(true);
    try {
      const number = await reserveNumber();
      const created = await finance.createInvoice({
        number,
        doc_type: docType,
        customer_name: customer.name.trim(),
        customer_phone: customer.phone.trim() || null,
        customer_email: customer.email.trim() || null,
        customer_address: customer.address.trim() || null,
        customer_ice: customer.ice.trim() || null,
        issue_date: issueDate,
        sale_date: saleDate || null,
        due_date: dueDate || null,
        reference: reference.trim() || null,
        payment_terms: paymentTerms.trim() || null,
        payment_method: paymentMethod || null,
        legal_mentions: mentions.trim() || null,
        status: 'draft',
        discount,
        tax_rate: taxRate,
        notes: notes.trim() || null,
        items: validLines.map((l) => ({
          description: l.description.trim(),
          quantity: Number(l.quantity) || 1,
          unit_price: Number(l.unit_price) || 0,
          discount: Number(l.discount) || 0,
          item_type: l.item_type,
          product_id: l.product_id,
          subtotal: Math.max(0, (Number(l.quantity) || 1) * (Number(l.unit_price) || 0) - (Number(l.discount) || 0)),
        })),
      });
      navigate(`${meta.basePath}?created=${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’enregistrer le document.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <Link to={meta.basePath} className="text-xs text-foreground-500 hover:text-foreground-800 flex items-center gap-1 mb-1 no-underline cursor-pointer">
            <i className="ri-arrow-left-line"></i>Retour aux {meta.plural.toLowerCase()}
          </Link>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className={`${meta.icon} text-primary-500`}></i>
            {meta.newLabel}
          </h2>
          <p className="text-sm text-foreground-500 mt-0.5">
            Document structuré en 5 blocs conformes : vendeur, client, références, lignes, totaux.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={meta.basePath}
            className="px-4 py-2 rounded-full border border-background-200/70 bg-background-50 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer no-underline"
          >
            Annuler
          </Link>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className="flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
          >
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            Enregistrer
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 px-4 py-2.5 rounded-lg bg-red-50 text-red-600 text-sm">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>{error}</span>
        </div>
      )}

      <InvoiceHeaderBlock profile={profile} />

      <InvoiceComplianceBlock
        profile={profile}
        customerName={customer.name}
        customerIce={customer.ice}
        taxRate={taxRate}
        isQuote={isQuote}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <ClientPickerBlock
            merchantId={merchantId}
            clients={clients}
            customer={customer}
            onChange={(patch) => setCustomer((prev) => ({ ...prev, ...patch }))}
            onClientCreated={reloadClients}
          />

          <LineItemsEditor
            lines={lines}
            onChange={setLines}
            currency={currency}
            catalog={catalog}
            catalogLoading={catalogLoading}
          />

          <InvoiceTotalsBlock
            totals={totals}
            discount={discount}
            onDiscount={setDiscount}
            taxRate={taxRate}
            onTaxRate={(v) => { taxTouched.current = true; setTaxRate(v); }}
            paymentTerms={paymentTerms}
            onPaymentTerms={setPaymentTerms}
            paymentMethod={paymentMethod}
            onPaymentMethod={setPaymentMethod}
            mentions={mentions}
            onMentions={setMentions}
            currency={currency}
            isQuote={isQuote}
            vatSubject={profile.vat_subject}
          />
        </div>

        <div className="space-y-5">
          <section className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
            <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2 mb-3">
              <i className="ri-hashtag text-primary-500"></i>
              Bloc 3 · Références
            </h3>

            <Field label="Numéro (automatique)">
              <div className={`${inputClass} flex items-center gap-2 bg-background-100 text-foreground-700`}>
                <i className="ri-lock-line text-foreground-400"></i>
                <span className="font-medium truncate">{numberPreview}</span>
              </div>
            </Field>

            <Field label="Date d’émission">
              <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className={inputClass} />
            </Field>
            <Field label="Date de la vente / prestation">
              <input type="date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} className={inputClass} />
            </Field>
            <Field label={isQuote ? 'Valable jusqu’au' : 'Échéance de paiement'}>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputClass} />
            </Field>
            <Field label="Bon de commande / devis lié">
              <input value={reference} onChange={(e) => setReference(e.target.value)} className={inputClass} placeholder="N° BC ou devis" />
            </Field>
            <Field label="Notes internes">
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500} className={inputClass} placeholder="Visible sur le document" />
            </Field>
          </section>

          <section className="bg-background-100 rounded-lg p-4 md:p-5 sticky top-4">
            <p className="text-xs text-foreground-500">Montant total à facturer</p>
            <p className="text-2xl font-bold font-heading text-foreground-950 mt-1">{formatMoney(totals.total, currency)}</p>
            <p className="text-[11px] text-foreground-500 mt-1">
              {validCount(lines)} ligne(s) · {customer.name || 'Client à renseigner'}
            </p>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="w-full mt-3 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer"
            >
              {busy ? <i className="ri-loader-4-line animate-spin"></i> : meta.createLabel}
            </button>
            <p className="text-[11px] text-foreground-400 mt-2 flex items-start gap-1">
              <i className="ri-information-line mt-0.5"></i>
              La numérotation est continue et gérée automatiquement.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

function validCount(lines: InvoiceLineDraft[]): number {
  return lines.filter((l) => l.description.trim()).length;
}